import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useSitStores, useStores} from "../../../../stores";
import {generatePath, useNavigate, useParams} from "react-router";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    TextField
} from "@mui/material";
import {InitiativeFinancialForm} from "./initiativeFinancialForm";
import {sitRoutes} from "../../../routing/sitRoutes";
import {InitiativePlaningFrom} from "./InitiativePlaningFrom";
import {InitiativeSuccessStepFrom} from "./InitiativeSuccessStepFrom";
import {
    AttachFile,
    CallSplit,
    Category,
    Check,
    ChevronLeft,
    Flag,
    Group,
    History,
    InfoOutlined,
    Inventory2,
    Payments,
    Schedule,
    UploadFile,
    Verified
} from "@mui/icons-material";
import EditOutlined from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import {InitiativeScopeForm} from "./initiativeScopeForm";
import {InitiativeGeneralForm} from "./initiativeGeneralForm";
import {InitiativeSkuForm} from "./initiativeSkuForm";
import {InitiativePeopleForm} from "./initiativePeopleForm";
import {InitiativeAttachmentForm} from "./initiativeAttachmentForm";
import {InitiativeAuditLogForm} from "./initiativeAuditLogForm";
import {InitiativeSplitForm} from "./initiativeSplitForm.tsx";
import {styled} from "@mui/material/styles";
import {InitiativeEvidenceForm} from "./initiativeEvidenceForm";
import type {Initiative} from "../../../ApiTypes";
import {CancellationReasonValues, DoiStatus} from "../../../ApiTypes";
import {cancellationReasonLabel, sitFieldLabel} from "../../../sitLabels";
import i18n from "../../../../i18n/i18n";
import {notificationStore} from "../../../../stores/init";
import {SitSection, SitDoiStepper, SitRichText, SitTrafficDot} from "../../../components/design/SitDesignAtoms";
// import {InitiativeKpiForm} from "./initiativeKpiForm";
// import {InitiativeTeamForm} from "./initiativeTeamForm";

// See https://mui.com/material-ui/react-button/#file-upload
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const DISCARD = 'You have unsaved changes. Leave and discard them?';
// Refresh the editing lock well within its 30 minutes
const LOCK_REFRESH_MS = 5 * 60 * 1000;
const SECTION_LINKS: [string, string][] = [
    ['general', 'General'], ['scope', 'Scope'], ['split', 'Split'], ['team', 'Team'], ['financials', 'Financials'],
    ['timeline', 'Timeline'], ['skus', 'SKUs'], ['milestones', 'Milestones'], ['evidence', 'Evidence'],
    ['attachments', 'Attachments'], ['activity', 'Activity'],
];

/** Scroll a form section to just below the sticky form header (which holds the section menu). */
function scrollToSection(id: string) {
    const section = document.getElementById('sit-sec-' + id);
    const head = document.querySelector('.sit-form-head');
    if (!section) return;
    const offset = (head?.getBoundingClientRect().bottom ?? 0) + 8;
    window.scrollTo({top: section.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth'});
}

export const InitiativeForm: React.FC = observer(() => {
    const {initiativeListStore, initiativeInstanceStore, attachmentStore, sitApi} = useSitStores();
    const {p} = useStores();
    const navigate = useNavigate();
    const params = useParams();
    const initiativeId: number | 'new' = 'initiativeId' in params ? parseInt(params['initiativeId'] ?? '') || 'new' : 'new';
    const approvalParam = 'approval' in params && params['approval'] === 'approval';
    const [isLoading, setIsLoading] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isLoadingApproval, setIsLoadingApproval] = React.useState(false);
    const [deactivateOpen, setDeactivateOpen] = React.useState(false);
    const [cancelReason, setCancelReason] = React.useState<number>(1);

    useEffect(() => {
        if (initiativeId === 'new')
            initiativeInstanceStore.setInstance({});
        else
            sitApi.getInitiative(initiativeId).then((resp) => {
                initiativeInstanceStore.setInstance(resp.data, approvalParam);
                initiativeInstanceStore.autoLock();
            }).catch((_reason) => {
                navigate(generatePath(sitRoutes.sit_initiatives));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiativeId]);

    // Keep the editing lock while the form is open (it runs out after 30 minutes without a refresh) and release it
    // when leaving the form any way, including reloading or closing the page.
    const holdsLock = initiativeInstanceStore.holdsLock;
    useEffect(() => {
        if (!holdsLock) return undefined;
        const timer = window.setInterval(() => initiativeInstanceStore.refreshLock(), LOCK_REFRESH_MS);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [holdsLock]);
    useEffect(() => {
        const onLeave = () => initiativeInstanceStore.releaseLockOnLeave();
        window.addEventListener('pagehide', onLeave);
        return () => {
            window.removeEventListener('pagehide', onLeave);
            initiativeInstanceStore.releaseLock(false);
        };
    }, [initiativeInstanceStore]);

    // Unsaved changes: ask before closing the tab or following a link out of the form.
    const dirty = initiativeInstanceStore.dirty;
    useEffect(() => {
        if (!dirty) return undefined;
        const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
        const onLink = (e: MouseEvent) => {
            const link = (e.target as HTMLElement).closest('a[href]');
            if (link && !link.closest('.sit-formscreen') && !window.confirm(DISCARD)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('beforeunload', onUnload);
        document.addEventListener('click', onLink, true);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
            document.removeEventListener('click', onLink, true);
        };
    }, [dirty]);

    // Save errors show under the header; bring them into view
    const errorsRef = React.useRef<HTMLDivElement>(null);
    const saveErrors = initiativeInstanceStore.save_errors;
    useEffect(() => {
        if (saveErrors?.length) errorsRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, [saveErrors]);

    const goBack = () => {
        if (initiativeInstanceStore.dirty && !window.confirm(DISCARD)) return;
        // Release possible lock, clear the form and return to the list.
        initiativeInstanceStore.releaseLock(false);
        initiativeInstanceStore.setSaveErrors(undefined);
        initiativeInstanceStore.setInstance({});
        navigate(generatePath(sitRoutes.sit_initiatives));
    };
    const holder = initiativeInstanceStore.lockHolder;
    const canEdit = !initiativeInstanceStore.creating && initiativeInstanceStore.disabled && !holder
        && !initiativeInstanceStore.instance.approved && !initiativeInstanceStore.readyForApproval;

    return (
        <div className="sit-formscreen">
            <form onSubmit={(event) => {
                event.preventDefault();
                const saving = initiativeInstanceStore.createOrUpdate();
                // No request when the approval dialog opens instead.
                if (!saving) return;
                setIsLoading(true);
                const isNew = !initiativeInstanceStore.instance.id;
                saving.then(() => {
                    initiativeInstanceStore.saved_snapshot = '';
                    initiativeInstanceStore.setSaveErrors(undefined);
                    initiativeInstanceStore.setInstance({});
                    initiativeListStore.reloadData();
                    notificationStore.show(isNew ? 'Initiative added.' : 'Initiative saved.',
                        {severity: 'success', autoHideDuration: 4000});
                    navigate(generatePath(sitRoutes.sit_initiatives));
                }).catch(error => {
                    initiativeInstanceStore.setSaveErrors(error.response.data);
                }).finally(() => {
                    setIsLoading(false);
                });
            }}>
                <div className="sit-form-head">
                    <button type="button" className="ac-icon-btn" title="Back to initiatives" onClick={goBack}>
                        <ChevronLeft/>
                    </button>
                    <div className="sit-form-head-mid">
                        <div className="sit-form-eyebrow">
                            {initiativeId === 'new'
                                ? "New initiative · Initiative details"
                                : `SIT-${initiativeId} · Initiative's details`}
                        </div>
                        <input className="sit-form-titleinput" placeholder="Untitled initiative" required
                               aria-label={sitFieldLabel('title')}
                               disabled={initiativeInstanceStore.disabled}
                               value={initiativeInstanceStore.instance.title ?? ''}
                               onChange={e => initiativeInstanceStore.title = e.target.value}/>
                    </div>

                    {initiativeId !== 'new' &&
                        <SitTrafficDot initiative={initiativeInstanceStore.instance as Initiative}/>}
                    {initiativeInstanceStore.instance.approved &&
                        <span className="sit-tag hue-good">Approved</span>}
                    {initiativeInstanceStore.instance.cancellation_reason != null &&
                        <span className="sit-tag hue-bad" title={cancellationReasonLabel(initiativeInstanceStore.instance.cancellation_reason)}>
                            {i18n.t('pill.deactivated', {ns: 'sit'})}
                        </span>}

                    {canEdit &&
                        <button type="button" className="ac-btn" onClick={() => initiativeInstanceStore.getLock()}>
                            <EditOutlined fontSize="small"/> Edit
                        </button>}

                    {!initiativeInstanceStore.disabled && !initiativeInstanceStore.readyForApproval && <>
                        {initiativeId !== 'new' && initiativeInstanceStore.instance.cancellation_reason == null &&
                            <button type="button" className="ac-btn ac-btn--danger" onClick={() => setDeactivateOpen(true)}>
                                Deactivate
                            </button>}
                        <button type="button" className="ac-btn" onClick={goBack}>Cancel</button>
                        <button type="submit" className="ac-btn ac-btn--primary"
                                disabled={isLoading || initiativeInstanceStore.disabled}>
                            {isLoading ? <CircularProgress size={14}/> : <Check fontSize="small"/>}
                            {initiativeId === 'new' ? 'Add initiative' : 'Update initiative'}
                        </button>
                    </>}

                    {initiativeInstanceStore.readyForApproval && <>
                        <button type="button" className="ac-btn ac-btn--danger"
                                onClick={() => {
                                    initiativeInstanceStore.approve(false)?.then(() => {
                                        initiativeListStore.reloadApprovalData();
                                        navigate(generatePath(sitRoutes.sit_initiatives));
                                    });
                                }}>Reject
                        </button>
                        <button type="button" className="ac-btn ac-btn--primary"
                                onClick={() => {
                                    initiativeInstanceStore.approve(true)?.then(() => {
                                        initiativeListStore.reloadApprovalData();
                                        navigate(generatePath(sitRoutes.sit_initiatives));
                                    });
                                }}>Accept
                        </button>
                    </>}
                    <nav className="sit-sec-nav" aria-label="Sections">
                        {SECTION_LINKS
                            .filter(([id]) => !initiativeInstanceStore.creating || !['attachments', 'activity'].includes(id))
                            .filter(([id]) => id !== 'skus' || !p.p.sitHideSkuForm)
                            .map(([id, label]) =>
                                <button key={id} type="button" className="sit-sec-link"
                                        onClick={() => scrollToSection(id)}>
                                    {label}
                                </button>)}
                    </nav>
                </div>

                <div className="sit-form-scroll">
                    <div className="sit-form-col">
                        {holder &&
                            <Alert severity="info" className="mb-2"
                                   action={initiativeInstanceStore.canTakeOver &&
                                       <Button color="inherit" size="small" onClick={() => initiativeInstanceStore.getLock(true)}>
                                           Take over
                                       </Button>}>
                                {holder.name || holder.email} is editing this initiative
                                {initiativeInstanceStore.instance.locked_at
                                    ? ` (since ${dayjs(initiativeInstanceStore.instance.locked_at).format('HH:mm')})` : ''}.
                                You can view it; it frees up when they finish, or after 30 minutes without activity.
                            </Alert>}
                        {saveErrors && saveErrors.length > 0 &&
                            <div ref={errorsRef}>
                                <Alert severity="error" color="error" className="mb-2">
                                    Please fix the following and try again
                                    <List>
                                        {saveErrors.map((err_item, index) =>
                                            <ListItem key={index} disablePadding>
                                                <ListItemText primary={err_item[0]} secondary={err_item[1]}/>
                                            </ListItem>
                                        )}
                                    </List>
                                </Alert>
                            </div>}
                        <div className="sit-details">
                            <div className="sit-det-stepper">
                                <SitDoiStepper doi={initiativeInstanceStore.instance.doi_status ?? 1}
                                               approvalState={initiativeInstanceStore.instance.approved ? 'approved' : null}
                                               onSelect={initiativeInstanceStore.disabled ? undefined
                                                   : stage => initiativeInstanceStore.doiStatus = DoiStatus[stage - 1]}/>
                            </div>

                            <SitSection icon={<InfoOutlined/>} id="sit-sec-general" title="General information" tint="a">
                                <InitiativeGeneralForm/>
                            </SitSection>

                            <SitSection icon={<Category/>} id="sit-sec-scope" title="Scope & classification" tint="b">
                                <InitiativeScopeForm/>
                            </SitSection>

                            <SitSection icon={<CallSplit/>} id="sit-sec-split" title="Saving split" tint="b"
                                        sub="For scope of multiple companies or categories, the savings need to be distributed among the participants.">
                                <InitiativeSplitForm/>
                            </SitSection>

                            <SitSection icon={<Group/>} id="sit-sec-team" title="Team & roles" tint="a">
                                <InitiativePeopleForm/>
                            </SitSection>

                            <SitSection icon={<Payments/>} id="sit-sec-financials" title="Financials" tint="c">
                                <InitiativeFinancialForm/>
                            </SitSection>

                            <SitSection icon={<Schedule/>} id="sit-sec-timeline" title="Timeline & planning" tint="a">
                                <InitiativePlaningFrom/>
                            </SitSection>

                            {!p.p.sitHideSkuForm &&
                            <SitSection icon={<Inventory2/>} id="sit-sec-skus" title="Related SKUs" tint="b">
                                <InitiativeSkuForm/>
                            </SitSection>}

                            <SitSection icon={<Flag/>} id="sit-sec-milestones" title="Key milestones" tint="a"
                                        sub="Key milestones that need to be achieved for realizing this initiative.">
                                <InitiativeSuccessStepFrom/>
                            </SitSection>

                            <SitSection icon={<Verified/>} id="sit-sec-evidence" title={sitFieldLabel('evidence')} tint="b"
                                        sub="External proof links (e.g. SharePoint) for audit.">
                                <InitiativeEvidenceForm/>
                            </SitSection>

                            {!initiativeInstanceStore.creating &&
                            <SitSection icon={<AttachFile/>} id="sit-sec-attachments" title="Attachments" tint="c"
                                        sub="Attach any documents, e.g., calculation of savings, presentations, etc."
                                        right={initiativeInstanceStore.instance.id && initiativeInstanceStore.instance.id > 0 &&
                                            <Button
                                                disabled={initiativeInstanceStore.disabled || isUploading}
                                                variant="outlined"
                                                component="label"
                                                role={undefined}
                                                startIcon={<UploadFile/>}>
                                                Upload Document
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    onChange={(event) => {
                                                        if (event.target.files && event.target.files.item(0) !== null) {
                                                            setIsUploading(true);
                                                            sitApi.createInitiativeAttachment(
                                                                initiativeInstanceStore.instance.id!, event.target.files.item(0)!
                                                            ).then(() => {
                                                                attachmentStore.initiativeAttachmentsRM.request({id: initiativeInstanceStore.instance.id!});
                                                                setIsUploading(false);
                                                            });
                                                        }
                                                    }}/>
                                            </Button>}>
                                <InitiativeAttachmentForm/>
                            </SitSection>}

                            {!initiativeInstanceStore.creating &&
                            <SitSection icon={<History/>} id="sit-sec-activity" title="Activity log" tint="a"
                                        sub="Who changed what on this initiative, and when.">
                                <InitiativeAuditLogForm/>
                            </SitSection>}
                        </div>

                        {/*<Grid container spacing={3}>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Typography variant="h3" component="h3">KPI For Monitoring</Typography>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Grid container spacing={3} className="mt-2" justifyContent="end">*/}
                        {/*            <Grid>*/}
                        {/*                <Button*/}
                        {/*                    disabled={initiativeInstanceStore.disabled}*/}
                        {/*                    variant="outlined"*/}
                        {/*                    startIcon={<Add/>}*/}
                        {/*                    onClick={() => initiativeInstanceStore.kpiDialogOpen = true}>*/}
                        {/*                    New KPI for monitoring*/}
                        {/*                </Button>*/}
                        {/*            </Grid>*/}
                        {/*        </Grid>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={12}>*/}
                        {/*        <InitiativeKpiForm/>*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}

                        {initiativeInstanceStore.readyForApproval && <Grid container spacing={3}>
                            <Grid size={12}>
                                <div className="sitf full sitf-rich mt-3">
                                    <span className="sitf-label">Approval notes</span>
                                    <SitRichText label="Approval notes" disabled resetKey="approval-notes"
                                                 value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}
                                                 onChange={() => undefined}/>
                                </div>
                            </Grid>
                        </Grid>}

                    </div>
                </div>
                {/*Approval */}
                <Dialog
                    open={initiativeInstanceStore.approval_dialog_open} aria-labelledby="dialog-title"
                    aria-describedby="dialog-description"
                    onClose={() => {
                        setIsLoadingApproval(false);
                        initiativeInstanceStore.approvalDialogOpen = false;
                    }}>
                    <DialogTitle id="dialog-title">
                        Approval Required
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="dialog-description">
                            Changing initiative status to
                            <b> "DOI5 - EBIT effective" </b>
                            requires approval from
                            <b> "{initiativeInstanceStore.instance.approver?.name}" </b>.
                            <br/>
                            Are you sure you want to submit this change?
                        </DialogContentText>
                        <Grid container className="mt-2">
                            <Grid size={12}>
                                <div className="sitf full sitf-rich">
                                    <span className="sitf-label">Approval notes</span>
                                    <SitRichText label="Approval notes" resetKey={`approval-${initiativeInstanceStore.load_count}`}
                                                 value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}
                                                 onChange={v => initiativeInstanceStore.description.approval_notes = v}/>
                                </div>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsLoadingApproval(false);
                            initiativeInstanceStore.approvalDialogOpen = false;
                        }}>cancel</Button>
                        <Button
                            variant="contained"
                            disabled={isLoadingApproval}
                            className="loading-button"
                            onClick={() => {
                                setIsLoadingApproval(true);
                                initiativeInstanceStore.createOrUpdate(true)?.then(() => {
                                    initiativeInstanceStore.setSaveErrors(undefined);
                                    initiativeListStore.reloadApprovalData();
                                    initiativeListStore.reloadData();
                                    notificationStore.show('Initiative sent for approval.',
                                        {severity: 'success', autoHideDuration: 4000});
                                    navigate(generatePath(sitRoutes.sit_initiatives));
                                }).catch(error => {
                                    initiativeInstanceStore.setSaveErrors(error.response?.data);
                                }).finally(() => {
                                    setIsLoadingApproval(false);
                                    initiativeInstanceStore.approvalDialogOpen = false;
                                });
                            }}>
                            Send for approval
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Deactivate / cancel — captures an InitiativeCancellationReason */}
                <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
                    <DialogTitle>Deactivate initiative</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Select a reason for cancelling this initiative. It will be flagged as
                            deactivated with the reason recorded.
                        </DialogContentText>
                        <FormControl fullWidth className="mt-3">
                            <TextField select label={sitFieldLabel('cancellationReason')} value={cancelReason}
                                       onChange={(e) => setCancelReason(Number(e.target.value))}>
                                {CancellationReasonValues.map(v =>
                                    <MenuItem key={v} value={v}>{cancellationReasonLabel(v)}</MenuItem>)}
                            </TextField>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                initiativeInstanceStore.cancellationReason = cancelReason;
                                initiativeInstanceStore.createOrUpdate()?.then(() => {
                                    initiativeInstanceStore.setSaveErrors(undefined);
                                    initiativeInstanceStore.setInstance({});
                                    initiativeListStore.reloadData();
                                    navigate(generatePath(sitRoutes.sit_initiatives));
                                }).catch(error => {
                                    initiativeInstanceStore.setSaveErrors(error.response.data);
                                }).finally(() => setDeactivateOpen(false));
                            }}>
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>
        </div>
    );
});